import { useQuery } from '@tanstack/react-query';
import { apiFetch, apiSearch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useInfiniteList } from './infiniteList.ts';
import { useSession } from '../auth/session.tsx';
import type {
  CompliancePage, DirectorPage, DirectorRow, ExecutiveSummary, FinancePage, MyDashboards, ReportRow,
} from './types.ts';

/**
 * How many rows a batch carries. **Measured, 17 September.**
 *
 * Two costs pull against each other, and neither is the one the first version of
 * this comment assumed.
 *
 * **The fetch is flat.** A request costs about 700ms whether it returns 25 rows
 * or 200, because row security is paid per row scanned and the counts cover the
 * whole set either way.
 *
 * **The append is not flat, and it does not scale with the batch — it scales
 * with the rows already on screen.** React reconciles the whole list on every
 * append, so the same 50 rows cost 33ms to add at 50 rows and 100ms at 350.
 * Measured frame-accurately in a visible tab, from the response resolving to the
 * frame that actually showed the rows (total ms, at matched depth):
 *
 *     rows on screen │  25  │  50  │ 100  │ 200
 *              ~100  │ 39.8 │ 42.7 │ 72.3 │   —
 *              ~200  │ 66.1 │ 74.9 │ 92.7 │ 136.6
 *              ~300  │ 79.1 │105.1 │107.2 │   —
 *          ~350-400  │ 76.3 │111.5 │159.0 │ 157.9
 *     worst longtask │  79  │  99  │ 144  │ 235
 *
 * So the earlier claim that "a smaller batch buys nothing" was wrong: a smaller
 * batch is cheaper per append at every depth. What it costs is continuity. The
 * sentinel's `rootMargin` is 800px and a row is 64px, so after a batch lands the
 * reader scrolls `(batch × 64) - 800` px before the next fetch fires — 800px at
 * batch 25, 2,400 at 50, 5,600 at 100, 12,000 at 200. At 25 the list is
 * therefore almost always fetching, against a 700ms round trip.
 *
 * **Fifty**: 25 is ruled out on that continuity, 200 on jank — 137 to 266ms an
 * append, eight to sixteen dropped frames. Against 100, fifty is cheaper per
 * append at every depth and still leaves about four seconds of reading between
 * fetches.
 *
 * **The append used to grow with the list, and no batch size could fix that**,
 * because the cost was React reconciling every mounted row: 111ms at 400 rows,
 * 532–597ms past row 2,000, with long tasks of 477–541ms.
 *
 * **Memoising the two row components fixed it** (17 September). The same append
 * now costs about **27ms at 400 rows, 44ms at 2,000 and 83ms at 2,600**, and
 * React's own work is flat with depth at roughly 25ms. What remains in `total`
 * is layout and paint against a taller document, not reconciliation. So the
 * batch size is chosen on the fetch and the continuity above, and the depth is
 * no longer the thing that decides it. The handover has both tables.
 */
export const LIST_BATCH = 50;

/**
 * What the quick search asks for at a time when it loads up to a row, rather
 * than navigating away from the list to reach it.
 *
 * Larger than a batch because it is not scrolling: a request costs what the
 * scan costs, about 700ms whether it carries fifty rows or a thousand, so the
 * wait is the number of requests, and where a row sits depends on the order the
 * view asked for. In the filed view, newest first, a client's first report sits
 * at row 354 in the median case and row 634 at the ninetieth: one slab either
 * way, against eight batches and thirteen. Awaiting approval runs the other way
 * and is the worse case, row 788 and row 2,003 — one slab and three, against
 * sixteen batches and forty-one.
 *
 * It matches `maxLimit` on the listing specs, which is the server's ceiling;
 * asking for more returns that many and the loop simply asks again.
 */
export const LOAD_SLAB = 1000;

/**
 * The role dashboards.
 *
 * Every list keeps its previous page on screen while the next filter loads,
 * as the client list does, so the pages must treat placeholder rows under a
 * failed request as no answer at all. See `ClientList` for why.
 */


/** Which dashboards the caller may open. Changes only when their roles do. */
export function useMyDashboards() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['me', 'dashboards'],
    queryFn: () => apiFetch<MyDashboards>('/me/dashboards', getToken),
    ...queryDefaults,
    staleTime: 5 * 60_000,
  });
}

export interface DirectorFilters {
  view: 'clients' | 'locations' | 'map';
  case_manager?: string;
  band?: string;
  never_seen?: 'true';
  exempt?: 'true';
  sort?: string;
}

/**
 * One request: the facility board, and the counts the client list's own first
 * batch would carry. `enabled` is false on the list, where the batches below
 * carry them instead, so only one of the two ever runs.
 */
export function useDirectorDashboard(params: DirectorFilters & { offset: number; limit: number }, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['dashboards', 'director', params],
    queryFn: () => apiSearch<DirectorPage>('/dashboards/director', params, getToken),
    ...queryDefaults,
    enabled,
    placeholderData: (previous) => previous,
  });
}

/**
 * The visits client list, in batches, growing as it is scrolled.
 *
 * The sort and every filter go to the server with each batch, so the order and
 * the population are the server's over the whole set. The filters are in the
 * key: change one and this is a different list, which starts again at the top.
 */
export function useDirectorList(filters: DirectorFilters, enabled: boolean) {
  return useInfiniteList<DirectorRow, DirectorPage>({
    key: ['dashboards', 'director', filters],
    path: '/dashboards/director',
    body: ({ limit, offset }) => ({ ...filters, offset, limit }),
    rowsOf: (page) => page.clients ?? [],
    batchSize: LIST_BATCH,
    slabSize: LOAD_SLAB,
    enabled,
  });
}

export interface ComplianceFilters {
  view: 'outstanding' | 'filed';
  state?: string;
  urgency?: string;
  approval?: 'awaiting' | 'approved';
  /** Report type and county compose: independent, combinable, every combination valid. */
  type?: string;
  county?: string;
  /** Deceased and discharged clients are shown unless this says otherwise. */
  closed?: 'hide';
  sort: string;
}

/**
 * The reports, in batches, growing as they are scrolled. The filed view is
 * 2,719 rows and gains a year of reports every May, so it is never all in the
 * page at once unless someone scrolls all of it.
 */
export function useComplianceList(filters: ComplianceFilters) {
  return useInfiniteList<ReportRow, CompliancePage>({
    key: ['dashboards', 'compliance', filters],
    path: '/dashboards/compliance',
    body: ({ limit, offset }) => ({ ...filters, offset, limit }),
    rowsOf: (page) => page.reports,
    batchSize: LIST_BATCH,
    slabSize: LOAD_SLAB,
  });
}

/** Every arrangement, on one scrolling page: no limit is sent, and the server returns them all. */
export function useFinanceDashboard(params: { sort: string }) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['dashboards', 'finance', params],
    queryFn: () => apiSearch<FinancePage>('/dashboards/finance', params, getToken),
    ...queryDefaults,
    placeholderData: (previous) => previous,
  });
}

/** Seconds to count on the full caseload; the page says it is counting. */
export function useExecutiveSummary() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['dashboards', 'executive'],
    queryFn: () => apiFetch<ExecutiveSummary>('/dashboards/executive', getToken),
    ...queryDefaults,
  });
}
